import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboard } from "@/lib/api";
import { RENTAL_STATUS_LABELS, RENTAL_STATUS_COLORS, formatDateTime } from "@/lib/constants";
import { Car, Receipt, ArrowCounterClockwise, FileText, PlusCircle, CaretRight } from "@phosphor-icons/react";

export default function Dashboard({ users }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { fetchDashboard().then(setData).catch(console.error); }, []);

  if (!data) return <div className="flex items-center justify-center h-full"><div className="text-sm text-slate-400">Loading dashboard...</div></div>;

  const stats = [
    { label: "Active Rentals", value: data.active_rentals || 0, icon: Receipt, color: "text-foreground", bg: "bg-secondary" },
    { label: "Vehicles Available", value: data.available_vehicles || 0, icon: Car, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Returns Due", value: data.returns_due || 0, icon: ArrowCounterClockwise, color: "text-red-600", bg: "bg-red-50" },
    { label: "Pending Signatures", value: data.pending_signatures || 0, icon: FileText, color: "text-amber-600", bg: "bg-amber-50" },
  ];

  return (
    <div className="h-full overflow-y-auto" data-testid="dashboard">
      <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight font-heading text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1 font-medium">Your rental operations at a glance</p>
          </div>
          <button data-testid="dashboard-new-rental" onClick={() => navigate("/rentals/new")}
            className="flex items-center gap-2 h-11 px-5 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 transition-colors">
            <PlusCircle weight="bold" className="w-5 h-5" /> New Rental
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}
                className="bg-white border border-slate-200 rounded-md p-5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
                <div className="flex items-center justify-between">
                  <div className={`w-10 h-10 ${s.bg} rounded-md flex items-center justify-center`}>
                    <Icon weight="duotone" className={`w-5 h-5 ${s.color}`} />
                  </div>
                  <span className="text-3xl font-black font-heading text-slate-900">{s.value}</span>
                </div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-3">{s.label}</p>
              </div>
            );
          })}
        </div>

        {/* Fleet Summary */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Fleet Status</h2>
            <button onClick={() => navigate("/fleet")} className="text-xs text-foreground font-bold hover:underline flex items-center gap-1">View Fleet <CaretRight weight="bold" className="w-3 h-3" /></button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.entries(data.vehicle_stats || {}).map(([status, count]) => (
              <div key={status} className="bg-slate-50 rounded-md p-3 text-center">
                <div className="text-2xl font-black font-heading text-slate-900">{count}</div>
                <div className="text-xs font-semibold text-slate-500 mt-1">{status.replace(/_/g,' ')}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Rentals */}
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">Recent Rentals</h2>
            <button onClick={() => navigate("/rentals")} className="text-xs text-foreground font-bold hover:underline flex items-center gap-1">View All <CaretRight weight="bold" className="w-3 h-3" /></button>
          </div>
          <div className="divide-y divide-slate-100">
            {(data.recent_rentals || []).length === 0 && (
              <div className="p-8 text-center text-sm text-slate-400">No rentals yet. Create your first rental to get started.</div>
            )}
            {(data.recent_rentals || []).map((r) => {
              const sc = RENTAL_STATUS_COLORS[r.status] || {};
              return (
                <button key={r.id} data-testid={`recent-rental-${r.rental_number}`}
                  onClick={() => navigate(`/rentals/${r.id}`)}
                  className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left">
                  <span className="font-mono font-bold text-sm text-foreground w-24 flex-shrink-0">{r.rental_number}</span>
                  <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{r.customer_name || "—"}</span>
                  <span className="text-xs text-slate-500 hidden sm:block w-48 truncate">{r.vehicle_display}</span>
                  <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md border" style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}>
                    {RENTAL_STATUS_LABELS[r.status] || r.status}
                  </span>
                  <span className="text-xs text-slate-400 w-28 text-right hidden md:block">{formatDateTime(r.created_at)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
